import React, {
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react'
import * as echarts from 'echarts'
import { safeChartInit } from '../../../utils/echarts'
import { Box } from '../../atoms/Box'
import { Text } from '../../atoms/Text'
import { Loading } from '../../atoms/Loading'
import { Button } from '../../atoms/Button'
import { ButtonGroup } from '../../molecules/ButtonGroup'
import { StatsContainer } from '../../molecules/StatsContainer'
import {
  ChartContainer,
  ChartGrid,
  ChartSection,
  ChartTitle,
  ErrorContainer,
  NotAvailable,
  PrintWrapper,
} from '../../molecules/ChartComponents'
import {
  formatPricePerSqm,
  formatPercentage,
  getChartColors,
  calculateKPIs,
  generateInsights,
  processTimeSeriesData,
  processPropertyCategoryData,
  processRegionalData,
  formatQuarter,
} from './NewVsResaleChart.utils'
import {
  NewVsResaleChartProps,
  NewVsResaleChartRef,
  ChartViewType,
} from './NewVsResaleChart.types'
import { StatCardVariant } from '../../atoms/StatCard/StatCard.types'

// Default translation function fallback
const defaultT = (key: string) => {
  const parts = key.split('.')
  return parts[parts.length - 1] || key
}

// Helper to translate property category names
const translateCategory = (
  category: string,
  t: (key: string) => string
): string => {
  const key = category.toLowerCase()
  if (key === 'apartment') return t('charts.categories.apartment')
  if (key === 'house') return t('charts.categories.house')
  if (key === 'all types' || key === 'all') return t('charts.labels.allTypes')
  return category
}

// When trailing quarters were dropped by min-sample-size filtering, extend the
// x-axis up to the last complete quarter so the timeline always reaches the
// present. Series data arrays are padded with null so lines stop where data
// stops, leaving an empty trailing segment on the axis.
const padTrailingQuarters = <T,>(
  labels: string[],
  lastDateIso: string | undefined,
  ...dataArrays: T[][]
): { labels: string[]; data: (T | null)[][] } => {
  const baseData = dataArrays.map((a) => [...a]) as (T | null)[][]
  if (!lastDateIso || labels.length === 0) {
    return { labels: [...labels], data: baseData }
  }
  const last = new Date(lastDateIso)
  const now = new Date()
  let endY = now.getFullYear()
  let endQ = Math.floor(now.getMonth() / 3) - 1
  if (endQ < 0) {
    endQ = 3
    endY -= 1
  }
  let q = Math.floor(last.getMonth() / 3)
  let y = last.getFullYear()
  const outLabels = [...labels]
  while (y < endY || (y === endY && q < endQ)) {
    q += 1
    if (q > 3) {
      q = 0
      y += 1
    }
    outLabels.push(formatQuarter(new Date(y, q * 3, 1).toISOString()))
    baseData.forEach((a) => a.push(null))
  }
  return { labels: outLabels, data: baseData }
}

export const NewVsResaleChart = forwardRef<
  NewVsResaleChartRef,
  NewVsResaleChartProps
>(
  (
    {
      data = [],
      locationValue = '',
      loading = false,
      error = null,
      priceType: controlledPriceType,
      onPriceTypeChange,
      hidePriceTypeButtons = false,
      isRental = false,
      priceMode,
      showKPIs = true,
      showInsights = true,
      showControls = true,
      accentColor = '#00ade6',
      t = defaultT,
    },
    ref
  ) => {
    // Build translatable VIEW_TYPES and PRICE_TYPES
    const VIEW_TYPES: ChartViewType[] = [
      { type: 'composition', label: t('charts.viewTypes.stacked') },
      { type: 'percentage', label: t('charts.viewTypes.percentage') },
      { type: 'absolute', label: t('charts.viewTypes.sideBySide') },
    ]

    const PRICE_TYPES = [
      { type: 'median' as const, label: t('charts.priceTypes.median') },
      { type: 'average' as const, label: t('charts.priceTypes.average') },
    ]
    const [currentView, setCurrentView] =
      useState<ChartViewType['type']>('percentage')
    const [internalPriceType, setInternalPriceType] = useState<
      'median' | 'average'
    >('median')
    const priceType = controlledPriceType ?? internalPriceType

    const handlePriceTypeChange = (type: 'median' | 'average') => {
      if (onPriceTypeChange) {
        onPriceTypeChange(type)
      } else {
        setInternalPriceType(type)
      }
    }
    const compositionChartRef = useRef<HTMLDivElement>(null)
    const premiumChartRef = useRef<HTMLDivElement>(null)
    const regionalChartRef = useRef<HTMLDivElement>(null)

    const compositionInstance = useRef<echarts.ECharts | null>(null)
    const premiumInstance = useRef<echarts.ECharts | null>(null)
    const regionalInstance = useRef<echarts.ECharts | null>(null)
    const isPrintingRef = useRef(false)

    useEffect(() => {
      if (!loading && !error && timeSeries.length > 0) {
        createCharts()
      }

      return () => {
        compositionInstance.current?.dispose()
        premiumInstance.current?.dispose()
        regionalInstance.current?.dispose()
      }
    }, [currentView, loading, error, priceMode])

    // Use ResizeObserver to properly resize charts when container size changes
    useEffect(() => {
      const resizeObserver = new ResizeObserver(() => {
        compositionInstance.current?.resize()
        premiumInstance.current?.resize()
        regionalInstance.current?.resize()
      })

      if (compositionChartRef.current) {
        resizeObserver.observe(compositionChartRef.current)
      }
      if (premiumChartRef.current) {
        resizeObserver.observe(premiumChartRef.current)
      }
      if (regionalChartRef.current) {
        resizeObserver.observe(regionalChartRef.current)
      }

      return () => resizeObserver.disconnect()
    }, [])

    // Process data
    const timeSeries = processTimeSeriesData(data)
    const categories = processPropertyCategoryData(data, priceType)
    const regional = processRegionalData(data, locationValue)

    // Calculate and set KPIs
    const kpis = calculateKPIs(data, priceType)

    // Get translated price type label
    const priceTypeLabel =
      priceType === 'median'
        ? t('charts.priceTypes.median')
        : t('charts.priceTypes.average')
    const priceUnitLabel = isRental
      ? t('charts.labels.rentPerM2Chf').replace(' (CHF)', '')
      : t('charts.labels.pricePerM2Chf').replace(' (CHF)', '')

    const kpiData = [
      {
        id: 'new-share',
        label: t('charts.newVsResale.kpis.newShare'),
        value: formatPercentage(kpis.newPercentage),
        variant: 'default' as StatCardVariant,
      },
      {
        id: 'avg-price-new',
        label: t('charts.newVsResale.kpis.priceNew', {
          priceType: priceTypeLabel,
          priceUnit: priceUnitLabel,
        }),
        value: formatPricePerSqm(kpis.avgPriceSqmNew, isRental, priceMode),
        variant: 'default' as StatCardVariant,
      },
      {
        id: 'avg-price-existing',
        label: t('charts.newVsResale.kpis.priceExisting', {
          priceType: priceTypeLabel,
          priceUnit: priceUnitLabel,
        }),
        value: formatPricePerSqm(kpis.avgPriceSqmExisting, isRental, priceMode),
        variant: 'default' as StatCardVariant,
      },
      {
        id: 'premium',
        label: t('charts.newVsResale.kpis.premium'),
        value: `${kpis.premium >= 0 ? '+' : ''}${kpis.premium.toFixed(1)}%`,
        variant: 'default' as StatCardVariant,
      },
    ]

    // Generate insights
    const insights = generateInsights(
      timeSeries,
      categories,
      regional,
      kpis,
      priceType,
      t,
      isRental,
      priceMode
    )

    // Expose methods via ref for PDF export
    useImperativeHandle(ref, () => ({
      exportChart: () => {
        if (compositionInstance.current) {
          compositionInstance.current.resize()
          return compositionInstance.current.getDataURL({
            type: 'png',
            pixelRatio: 3,
            backgroundColor: '#fff',
            excludeComponents: ['toolbox', 'dataZoom'],
          })
        }
        return null
      },
      exportPremiumChart: () => {
        if (premiumInstance.current) {
          premiumInstance.current.resize()
          return premiumInstance.current.getDataURL({
            type: 'png',
            pixelRatio: 3,
            backgroundColor: '#fff',
            excludeComponents: ['toolbox', 'dataZoom'],
          })
        }
        return null
      },
      exportRegionalChart: () => {
        if (regionalInstance.current) {
          regionalInstance.current.resize()
          return regionalInstance.current.getDataURL({
            type: 'png',
            pixelRatio: 3,
            backgroundColor: '#fff',
            excludeComponents: ['toolbox', 'dataZoom'],
          })
        }
        return null
      },
      getKPIs: () => kpiData,
      getInsights: () => insights,
    }))

    const createCharts = () => {
      if (
        !compositionChartRef.current ||
        !premiumChartRef.current ||
        !regionalChartRef.current
      )
        return

      createCompositionChart()
      createPremiumChart()
      createRegionalChart()
    }

    const createCompositionChart = () => {
      if (!compositionChartRef.current) return

      if (compositionInstance.current) {
        compositionInstance.current.dispose()
      }

      compositionInstance.current = safeChartInit(
        compositionChartRef.current,
        undefined,
        {
          renderer: 'canvas',
          devicePixelRatio: isPrintingRef.current
            ? 1
            : window.devicePixelRatio || 1,
        }
      )

      if (isPrintingRef.current) {
        setTimeout(() => {
          compositionInstance.current?.resize()
        }, 50)
      }

      const colors = getChartColors()
      const option = getCompositionChartOption(colors)
      compositionInstance.current.setOption(option)
    }

    const getCompositionChartOption = (colors: any) => {
      const baseLabels = timeSeries.map((d) => formatQuarter(d.date))
      const newCounts = timeSeries.map((d) => d.newCount)
      const existingCounts = timeSeries.map((d) => d.existingCount)
      const lastDate = timeSeries[timeSeries.length - 1]?.date
      const padded = padTrailingQuarters<number>(
        baseLabels,
        lastDate,
        newCounts,
        existingCounts
      )
      const xAxisData = padded.labels
      const [paddedNewCounts, paddedExistingCounts] = padded.data

      switch (currentView) {
        case 'percentage':
          return createPercentageOption(
            xAxisData,
            paddedNewCounts,
            paddedExistingCounts,
            colors
          )
        case 'absolute':
          return createAbsoluteOption(
            xAxisData,
            paddedNewCounts,
            paddedExistingCounts,
            colors
          )
        default:
          return createStackedOption(
            xAxisData,
            paddedNewCounts,
            paddedExistingCounts,
            colors
          )
      }
    }

    // Translated legend labels
    const newConstructionLabel = t('charts.newVsResale.legends.newConstruction')
    const existingPropertiesLabel = t(
      'charts.newVsResale.legends.existingProperties'
    )
    const numberOfListingsLabel = t('charts.labels.numberOfListings')
    const percentageLabel = t('charts.labels.percentage')
    const totalLabel = t('charts.newVsResale.tooltips.total')

    const createStackedOption = (
      xAxisData: string[],
      newCountData: (number | null)[],
      existingCountData: (number | null)[],
      colors: any
    ) => {
      return {
        tooltip: {
          trigger: 'axis',
          appendTo: () => document.body,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderColor: '#e5e7eb',
          borderWidth: 1,
          borderRadius: 8,
          formatter: (params: any) => {
            if (params[0]?.value == null || params[1]?.value == null) {
              return ''
            }
            const total = params[0].value + params[1].value
            return `${params[0].axisValue}<br/>
                  ${newConstructionLabel}: ${params[0].value.toLocaleString()} (${((params[0].value / total) * 100).toFixed(1)}%)<br/>
                  ${existingPropertiesLabel}: ${params[1].value.toLocaleString()} (${((params[1].value / total) * 100).toFixed(1)}%)<br/>
                  ${totalLabel}: ${total.toLocaleString()}`
          },
        },
        legend: {
          data: [newConstructionLabel, existingPropertiesLabel],
          bottom: 0,
          textStyle: { fontSize: 14 },
        },
        grid: {
          left: 10,
          right: 10,
          top: 20,
          bottom: 60,
          containLabel: true,
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: xAxisData,
          axisLine: {
            lineStyle: { color: '#e5e7eb' },
          },
          axisLabel: {
            color: '#6c757d',
          },
        },
        yAxis: {
          type: 'value',
          name: numberOfListingsLabel,
          nameTextStyle: {
            color: '#6c757d',
            padding: [0, 0, 0, 40],
          },
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: {
            color: '#6c757d',
          },
          splitLine: {
            lineStyle: {
              color: '#f0f0f0',
              type: 'dashed',
            },
          },
        },
        series: [
          {
            name: newConstructionLabel,
            type: 'line',
            stack: 'Total',
            smooth: true,
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: `${colors.new}33` },
                { offset: 1, color: `${colors.new}0D` },
              ]),
            },
            lineStyle: {
              color: colors.new,
              width: 3,
            },
            itemStyle: {
              color: colors.new,
            },
            emphasis: {
              focus: 'series',
            },
            data: newCountData,
          },
          {
            name: existingPropertiesLabel,
            type: 'line',
            stack: 'Total',
            smooth: true,
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: `${colors.existing}33` },
                { offset: 1, color: `${colors.existing}0D` },
              ]),
            },
            lineStyle: {
              color: colors.existing,
              width: 3,
            },
            itemStyle: {
              color: colors.existing,
            },
            emphasis: {
              focus: 'series',
            },
            data: existingCountData,
          },
        ],
      }
    }

    const createPercentageOption = (
      xAxisData: string[],
      newCountData: (number | null)[],
      existingCountData: (number | null)[],
      colors: any
    ) => {
      const percentageData = newCountData.map((nc, i) => {
        const ec = existingCountData[i]
        if (nc == null || ec == null) {
          return { new: null, existing: null }
        }
        const total = nc + ec
        return {
          new: total > 0 ? (nc / total) * 100 : 0,
          existing: total > 0 ? (ec / total) * 100 : 0,
        }
      })

      return {
        tooltip: {
          trigger: 'axis',
          appendTo: () => document.body,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderColor: '#e5e7eb',
          borderWidth: 1,
          borderRadius: 8,
          formatter: (params: any) => {
            if (params[0]?.value == null || params[1]?.value == null) {
              return ''
            }
            return `${params[0].axisValue}<br/>
                  ${newConstructionLabel}: ${params[0].value.toFixed(1)}%<br/>
                  ${existingPropertiesLabel}: ${params[1].value.toFixed(1)}%`
          },
        },
        legend: {
          data: [newConstructionLabel, existingPropertiesLabel],
          bottom: 0,
          textStyle: { fontSize: 14 },
        },
        grid: {
          left: 10,
          right: 10,
          top: 20,
          bottom: 60,
          containLabel: true,
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: xAxisData,
          axisLine: {
            lineStyle: { color: '#e5e7eb' },
          },
          axisLabel: {
            color: '#6c757d',
          },
        },
        yAxis: {
          type: 'value',
          name: percentageLabel,
          nameTextStyle: {
            color: '#6c757d',
            padding: [0, 0, 0, 40],
          },
          min: 0,
          max: 100,
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: {
            color: '#6c757d',
            formatter: '{value}%',
          },
          splitLine: {
            lineStyle: {
              color: '#f0f0f0',
              type: 'dashed',
            },
          },
        },
        series: [
          {
            name: newConstructionLabel,
            type: 'line',
            stack: 'Total',
            smooth: true,
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: `${colors.new}33` },
                { offset: 1, color: `${colors.new}0D` },
              ]),
            },
            lineStyle: {
              color: colors.new,
              width: 3,
            },
            itemStyle: {
              color: colors.new,
            },
            emphasis: {
              focus: 'series',
            },
            data: percentageData.map((d) => d.new),
          },
          {
            name: existingPropertiesLabel,
            type: 'line',
            stack: 'Total',
            smooth: true,
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: `${colors.existing}33` },
                { offset: 1, color: `${colors.existing}0D` },
              ]),
            },
            lineStyle: {
              color: colors.existing,
              width: 3,
            },
            itemStyle: {
              color: colors.existing,
            },
            emphasis: {
              focus: 'series',
            },
            data: percentageData.map((d) => d.existing),
          },
        ],
      }
    }

    const createAbsoluteOption = (
      xAxisData: string[],
      newCountData: (number | null)[],
      existingCountData: (number | null)[],
      colors: any
    ) => {
      return {
        tooltip: {
          trigger: 'axis',
          appendTo: () => document.body,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderColor: '#e5e7eb',
          borderWidth: 1,
          borderRadius: 8,
          formatter: (params: any) => {
            if (params[0]?.value == null || params[1]?.value == null) {
              return ''
            }
            return `${params[0].axisValue}<br/>
                  ${newConstructionLabel}: ${params[0].value.toLocaleString()}<br/>
                  ${existingPropertiesLabel}: ${params[1].value.toLocaleString()}`
          },
        },
        legend: {
          data: [newConstructionLabel, existingPropertiesLabel],
          bottom: 0,
          textStyle: { fontSize: 14 },
        },
        grid: {
          left: 10,
          right: 10,
          top: 20,
          bottom: 60,
          containLabel: true,
        },
        xAxis: {
          type: 'category',
          data: xAxisData,
          axisLine: {
            lineStyle: { color: '#e5e7eb' },
          },
          axisLabel: {
            color: '#6c757d',
          },
        },
        yAxis: {
          type: 'value',
          name: numberOfListingsLabel,
          nameTextStyle: {
            color: '#6c757d',
            padding: [0, 0, 0, 40],
          },
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: {
            color: '#6c757d',
          },
          splitLine: {
            lineStyle: {
              color: '#f0f0f0',
              type: 'dashed',
            },
          },
        },
        series: [
          {
            name: newConstructionLabel,
            type: 'line',
            smooth: true,
            symbol: 'circle',
            symbolSize: 6,
            lineStyle: {
              color: colors.new,
              width: 3,
            },
            itemStyle: {
              color: colors.new,
            },
            emphasis: {
              focus: 'series',
              lineStyle: { width: 4 },
            },
            data: newCountData,
          },
          {
            name: existingPropertiesLabel,
            type: 'line',
            smooth: true,
            symbol: 'circle',
            symbolSize: 6,
            lineStyle: {
              color: colors.existing,
              width: 3,
            },
            itemStyle: {
              color: colors.existing,
            },
            emphasis: {
              focus: 'series',
              lineStyle: { width: 4 },
            },
            data: existingCountData,
          },
        ],
      }
    }

    // Get price per m² label for premium chart
    const pricePerM2Label = isRental
      ? t('charts.labels.rentPerM2Chf')
      : t('charts.labels.pricePerM2Chf')
    const premiumLabel = t('charts.newVsResale.tooltips.premiumLabel')
    const newLabel = t('charts.newVsResale.tooltips.new')
    const existingLabel = t('charts.newVsResale.tooltips.existing')

    const createPremiumChart = () => {
      if (!premiumChartRef.current) return

      if (premiumInstance.current) {
        premiumInstance.current.dispose()
      }

      premiumInstance.current = safeChartInit(
        premiumChartRef.current,
        undefined,
        {
          renderer: 'canvas',
          devicePixelRatio: isPrintingRef.current
            ? 1
            : window.devicePixelRatio || 1,
        }
      )

      if (isPrintingRef.current) {
        setTimeout(() => {
          premiumInstance.current?.resize()
        }, 50)
      }

      const colors = getChartColors()
      const option = {
        tooltip: {
          trigger: 'axis',
          appendTo: () => document.body,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderColor: '#e5e7eb',
          borderWidth: 1,
          borderRadius: 8,
          axisPointer: {
            type: 'shadow',
          },
          formatter: (params: any) => {
            const category = params[0].axisValue
            const newPriceSqm = params[0].value
            const existingPriceSqm = params[1].value
            const premium = (
              ((newPriceSqm - existingPriceSqm) / existingPriceSqm) *
              100
            ).toFixed(1)
            return `${category}<br/>
                  ${newLabel}: ${formatPricePerSqm(newPriceSqm, isRental, priceMode)}<br/>
                  ${existingLabel}: ${formatPricePerSqm(existingPriceSqm, isRental, priceMode)}<br/>
                  ${premiumLabel}: ${premium}%`
          },
        },
        legend: {
          data: [newConstructionLabel, existingPropertiesLabel],
          bottom: 0,
          textStyle: { fontSize: 14 },
        },
        grid: {
          left: 10,
          right: 10,
          top: 20,
          bottom: 60,
          containLabel: true,
        },
        xAxis: {
          type: 'category',
          data: categories.map((d) => translateCategory(d.category, t)),
          axisLine: {
            lineStyle: { color: '#e5e7eb' },
          },
          axisLabel: {
            color: '#6c757d',
            interval: 0,
          },
        },
        yAxis: {
          type: 'value',
          name: pricePerM2Label,
          nameTextStyle: {
            color: '#6c757d',
            padding: [0, 0, 0, 40],
          },
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: {
            color: '#6c757d',
            formatter: (value: number) => value.toLocaleString(),
          },
          splitLine: {
            lineStyle: {
              color: '#f0f0f0',
              type: 'dashed',
            },
          },
        },
        series: [
          {
            name: newConstructionLabel,
            type: 'bar',
            barWidth: '30%',
            itemStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: colors.new },
                { offset: 1, color: colors.newLight },
              ]),
              borderRadius: [4, 4, 0, 0],
            },
            emphasis: {
              itemStyle: {
                color: '#1e40af',
              },
            },
            data: categories.map((d) => d.newPriceSqm),
          },
          {
            name: existingPropertiesLabel,
            type: 'bar',
            barWidth: '30%',
            itemStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: colors.existing },
                { offset: 1, color: colors.existingLight },
              ]),
              borderRadius: [4, 4, 0, 0],
            },
            emphasis: {
              itemStyle: {
                color: '#059669',
              },
            },
            data: categories.map((d) => d.existingPriceSqm),
          },
        ],
      }

      premiumInstance.current.setOption(option)
    }

    const createRegionalChart = () => {
      if (!regionalChartRef.current) return

      if (regionalInstance.current) {
        regionalInstance.current.dispose()
      }

      regionalInstance.current = safeChartInit(
        regionalChartRef.current,
        undefined,
        {
          renderer: 'canvas',
          devicePixelRatio: isPrintingRef.current
            ? 1
            : window.devicePixelRatio || 1,
        }
      )

      if (isPrintingRef.current) {
        setTimeout(() => {
          regionalInstance.current?.resize()
        }, 50)
      }

      const colors = getChartColors()
      const option = {
        tooltip: {
          trigger: 'axis',
          appendTo: () => document.body,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderColor: '#e5e7eb',
          borderWidth: 1,
          borderRadius: 8,
          axisPointer: {
            type: 'shadow',
          },
          formatter: (params: any) => {
            const name = params[0].axisValue
            const newCount = params[0].value
            const existingCount = params[1].value
            const total = newCount + existingCount
            return `${name}<br/>
                  ${newLabel}: ${newCount.toLocaleString()} (${((newCount / total) * 100).toFixed(1)}%)<br/>
                  ${existingLabel}: ${existingCount.toLocaleString()} (${((existingCount / total) * 100).toFixed(1)}%)<br/>
                  ${totalLabel}: ${total.toLocaleString()}`
          },
        },
        legend: {
          data: [newConstructionLabel, existingPropertiesLabel],
          bottom: 0,
          textStyle: { fontSize: 14 },
        },
        grid: {
          left: 10,
          right: 10,
          top: 20,
          bottom: 60,
          containLabel: true,
        },
        xAxis: {
          type: 'value',
          name: numberOfListingsLabel,
          nameTextStyle: {
            color: '#6c757d',
          },
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: {
            color: '#6c757d',
          },
          splitLine: {
            lineStyle: {
              color: '#f0f0f0',
              type: 'dashed',
            },
          },
        },
        yAxis: {
          type: 'category',
          data: regional.map((d) => d.name).reverse(),
          axisLine: {
            lineStyle: {
              color: '#e5e7eb',
            },
          },
          axisLabel: {
            color: '#6c757d',
          },
        },
        series: [
          {
            name: newConstructionLabel,
            type: 'bar',
            stack: 'total',
            barWidth: '60%',
            itemStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
                { offset: 0, color: colors.newLight },
                { offset: 1, color: colors.new },
              ]),
              borderRadius: [0, 4, 4, 0],
            },
            emphasis: {
              itemStyle: {
                color: '#1e40af',
              },
            },
            data: regional.map((d) => d.newCount).reverse(),
          },
          {
            name: existingPropertiesLabel,
            type: 'bar',
            stack: 'total',
            barWidth: '60%',
            itemStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
                { offset: 0, color: colors.existingLight },
                { offset: 1, color: colors.existing },
              ]),
              borderRadius: [0, 4, 4, 0],
            },
            emphasis: {
              itemStyle: {
                color: '#059669',
              },
            },
            data: regional.map((d) => d.existingCount).reverse(),
          },
        ],
      }

      regionalInstance.current.setOption(option)
    }

    useEffect(() => {
      // Match the Building Permits report, which prints cleanly: do NOT touch
      // the charts on print. The previous beforeprint/afterprint/matchMedia
      // handlers called chart.resize() + setOption() *during* the print
      // snapshot, which made Chrome serialise the canvas as a masked vector
      // figure (the white boxes in macOS Preview / Quick Look). Building
      // Permits charts do nothing on print and rasterise cleanly. The toolbox
      // is already hidden at creation (showToolbox=false in the report) and the
      // print layout keeps the same width, so no print-time resize is needed.
      const handleResize = () => {
        compositionInstance.current?.resize()
        premiumInstance.current?.resize()
        regionalInstance.current?.resize()
      }

      window.addEventListener('resize', handleResize)

      return () => {
        window.removeEventListener('resize', handleResize)
      }
    }, [])

    if (loading) {
      return (
        <Box style={{ textAlign: 'center', padding: '40px' }}>
          <Loading variant="spinner" size="large" center />
          <Text variant="body1" color="secondary" style={{ marginTop: '16px' }}>
            {t('charts.states.loadingNewVsResale')}
          </Text>
        </Box>
      )
    }

    if (error) {
      return (
        <ErrorContainer variant="error">
          <Text variant="body1">{error}</Text>
        </ErrorContainer>
      )
    }

    if (data.length === 0) {
      return (
        <NotAvailable
          title={t('charts.empty.title')}
          description={t('charts.empty.newVsResale')}
        />
      )
    }

    return (
      <PrintWrapper>
        {showControls && !hidePriceTypeButtons && (
          <Box marginBottom="24px">
            <Box display="flex" justifyContent="flex-end">
              <ButtonGroup>
                {PRICE_TYPES.map((pt) => (
                  <Button
                    key={pt.type}
                    variant={priceType === pt.type ? 'primary' : 'secondary'}
                    size="small"
                    onClick={() => handlePriceTypeChange(pt.type)}
                  >
                    {pt.label}
                  </Button>
                ))}
              </ButtonGroup>
            </Box>
          </Box>
        )}
        {showKPIs && kpiData.length > 0 && (
          <Box marginBottom="30px">
            <StatsContainer fullWidth stats={kpiData} />
          </Box>
        )}

        <ChartGrid columns={2}>
          <ChartSection>
            {showControls && (
              <Box marginBottom="16px">
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  flexWrap="wrap"
                  gap="12px"
                >
                  <ChartTitle style={{ fontSize: '14px' }}>
                    {t('charts.newVsResale.titles.composition')}
                  </ChartTitle>
                  <ButtonGroup>
                    {VIEW_TYPES.map((view) => (
                      <Button
                        key={view.type}
                        variant={
                          currentView === view.type ? 'primary' : 'secondary'
                        }
                        size="small"
                        onClick={() => setCurrentView(view.type)}
                      >
                        {view.label}
                      </Button>
                    ))}
                  </ButtonGroup>
                </Box>
              </Box>
            )}
            <ChartContainer height={350} ref={compositionChartRef} />
          </ChartSection>
          <ChartSection
            title={t('charts.newVsResale.titles.premium', {
              priceType: isRental
                ? t('charts.labels.rent')
                : t('charts.labels.price'),
            })}
          >
            <ChartContainer height={350} ref={premiumChartRef} />
          </ChartSection>
        </ChartGrid>

        <Box marginBottom="30px">
          <ChartSection
            title={t('charts.newVsResale.titles.activity', {
              location: locationValue,
            })}
          >
            <ChartContainer height={300} ref={regionalChartRef} />
          </ChartSection>
        </Box>

        {showInsights && insights.length > 0 && (
          <Box marginTop="30px">
            <ChartTitle style={{ marginBottom: '24px' }}>
              {t('charts.insights.keyInsights')}
            </ChartTitle>
            <Box display="flex" flexWrap="wrap" gap="24px">
              {insights.map((insight, index) => (
                <Box
                  key={index}
                  flex="1 1 280px"
                  style={{
                    background: '#f8f9fa',
                    borderRadius: '12px',
                    padding: '20px',
                    borderLeft: `4px solid ${accentColor}`,
                    minWidth: 0,
                  }}
                >
                  <Text
                    variant="h4"
                    style={{
                      fontSize: '16px',
                      fontWeight: 600,
                      marginBottom: '8px',
                      color: '#1a1a1a',
                    }}
                  >
                    {insight.title}
                  </Text>
                  <Text
                    variant="body2"
                    style={{
                      fontSize: '14px',
                      lineHeight: 1.5,
                      color: '#6c757d',
                    }}
                  >
                    {insight.description}
                  </Text>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </PrintWrapper>
    )
  }
)

NewVsResaleChart.displayName = 'NewVsResaleChart'
